import { X } from "lucide-react";
import { semanticColors, spacing, heights, layout, typography, iconSize, iconStroke, shadows, overlayMaxWidth } from "@/styles/tokens";

// Shared shell for the four record detail/edit panels (Product, BrandLock,
// Discount, FreightRate). Every style value here is copied verbatim from
// those four panels, which were byte-identical in their shell CSS before
// this extraction — see the refactor/shared-panel investigation report.
//
// Deliberately NOT used by SidePanel (quote analysis): that panel stays
// mounted and toggles between a full view and a collapsed rail (preserving
// its own accordion open/closed state across the toggle), while these four
// are conditionally rendered by their parent page and unmount on close.
// Different lifecycle, different header (title+close vs. static label+
// collapse chevron), different footer (row Cancel/Save vs. column stacked
// buttons). Forcing SidePanel onto this shell would risk losing its
// in-place collapse behavior. Do not merge it in here.
export function DetailPanel({
  testIdPrefix,
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  testIdPrefix: string;
  title: React.ReactNode;
  subtitle: React.ReactNode;
  onClose: () => void;
  footer: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <aside
      data-testid={`${testIdPrefix}-detail-panel`}
      style={{
        width: layout.panelWidthDetail,
        // Inert at any width >= 436px -- see overlayMaxWidth.
        maxWidth: overlayMaxWidth,
        height: "100%",
        borderLeft: `1px solid ${semanticColors.border}`,
        backgroundColor: semanticColors.surface,
        boxShadow: shadows.sm,
        display: "flex", flexDirection: "column",
        flexShrink: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: `${spacing[16]}px ${spacing[20]}px`, borderBottom: `1px solid ${semanticColors.border}`,
          display: "flex", alignItems: "flex-start", gap: spacing[12],
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            data-testid={`${testIdPrefix}-panel-title`}
            style={{
              fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
              letterSpacing: typography.letterSpacing.tight,
              overflow: "hidden", textOverflow: "ellipsis",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: typography.fontSize.sm, color: semanticColors.textMuted, marginTop: spacing[2],
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {subtitle}
          </div>
        </div>
        <button
          data-testid={`${testIdPrefix}-panel-close`}
          onClick={onClose}
          title="Close"
          style={{
            width: heights.iconButton, height: heights.iconButton, padding: 0, cursor: "pointer",
            background: "transparent", border: "none", color: semanticColors.textSecondary,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <X size={iconSize.lg} strokeWidth={iconStroke.regular} />
        </button>
      </div>

      {/* Body */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: spacing[20] }}>
        {children}
      </div>

      {/* Footer buttons */}
      <div
        style={{
          borderTop: `1px solid ${semanticColors.border}`,
          padding: spacing[16],
          display: "flex", gap: spacing[8], justifyContent: "flex-end",
          backgroundColor: semanticColors.surface,
        }}
      >
        {footer}
      </div>
    </aside>
  );
}
